import React, { Component, Fragment, type JSX } from 'react';
import { Field, FieldProps } from 'formik';
import { CaretakerFormSection } from './Form/CaretakerFormSection';
import { CheckoutStateResponse, CheckoutSubmitResponse, FormValues, Gender, Type } from './Checkout.interface';
import { PupilFormSection } from './Form/PupilFormSection';
import { Checkbox } from '../../formik/Checkbox';
import { validateRequired } from '../../formik/validate.formik';
import { DonationComponent } from '../donation/Donation.component';
import { AddressFields } from '../../formik/AddressFields';
import { Input } from '../../formik/Input';
import { InvoiceSection } from './Form/InvoiceSection';
import { CartList } from './CartList/CartList';
import { CalculationResponse } from './Form/CouponForm';
import { Wizard } from '../../formik/Wizard';
import { ajaxLink, ajaxNonce } from '../../App';
import { events } from '../../util/Events';
import { CART_UPDATED } from '../Cart/Cart';
import { EmptyShoppingCart } from './CheckoutEmpty';
import { CheckoutPlaceholder } from './CheckoutPlaceholder';
import { InitialCheckoutFormValues } from './Checkout.values';
import { ScrollTopComponent } from '../ScrollTop/ScrollTopComponent';

type Total = CalculationResponse['total'];

export interface CheckoutProps {}

declare const postID: number;

/**
 * This component handles the entire checkout form (page). The component
 * contains the 2 form steps, the stepper and the cart list. The validation
 * of the form is handled both on field level and using yup. The latter only
 * handles the conditional validation.
 *
 * The state of the component is determined/tracked in the backend. The response
 * of the GET getCheckout call is directly mapped to the state of this component.
 */
export class Checkout extends Component<CheckoutProps, CheckoutStateResponse> {

  constructor(props: CheckoutProps) {
    super(props);

    this.state = {
      orderType: null,
      cartList: undefined,
      donation: null,
      submenu: null,
      isPreRegisterOnly: null,
      pageExtSearchResults: null,
      total: null,
      notice: null,
      error: null,
      session: InitialCheckoutFormValues,
    };
  }

  /**
   * Fetch the checkout page content. When an addOn gets
   * removed of added the CART_UPDATED event will be emitted.
   * In this case fetch the latest checkout state.
   */
  public componentDidMount(): void {
    this.getCheckout(true);

    events.on(CART_UPDATED, () => this.getCheckout());
  }

  /**
   * Remove slashes from a string
   */
  private unescapeString(input: string): string {
    input = input.replace(/\\/g, '');
    return input;
  }

  /**
   * Remove slashes from an object
   */
  private unescapeObject(input: any): any {
    const clone = { ...input };
    for (const [key, value] of Object.entries(clone)) {
      if (typeof value === 'string') {
        clone[key] = this.unescapeString(value);
      } else if (value !== null && typeof value === 'object') {
        clone[key] = this.unescapeObject(value);
      }
    }
    return clone;
  }

  /**
   * Remove slashes from a session values
   */
  private unescapeSession(session: FormValues): FormValues {
    return this.unescapeObject(session);
  }

  /**
   * Fetch the checkout state. This call contains errors (optionally),
   * form values stored in the session, the cart list and the available
   * donation options.
   */
  private async getCheckout(initial: boolean = false, valueOverwrite: FormValues = null): Promise<void> {
    const response: CheckoutStateResponse = await this.post('Checkout', 'getCheckout');
    if (response.session && initial){
      response.session = this.unescapeSession(response.session);
      response.session.terms = false;
      response.session.newsletter = false;
    }
    if (response.cartList.length > 1 && this.checkOverlap(response.cartList)){
      const message: string = '<i class="fa fa-exclamation-triangle"></i> '
        + 'Let op! Er zijn trainingen geselecteerd die (deels) op dezelfde data plaatsvinden.';
      if (response.notice === null){
        response.notice = message;
      } else {
        response.notice += '<br />' + message;
      }
    }
    if (valueOverwrite){
      response.session = valueOverwrite;
      response.session.total = response.total;
    }
    this.setState(response);
  }

  /**
   * Compare Cartlist data and see if there is any overlap
   */
  private checkOverlap(cartlist: any[]): boolean {
    let overlap = false;
    for (let i = 0; i < cartlist.length; i++){
      const startDate = new Date(cartlist[i].datum_start);
      const endDate = new Date(cartlist[i].datum_eind);
      if (i < (cartlist.length - 1)){
        for (let x = i+1; x < cartlist.length; x++) {
          const comparedStartDate = new Date(cartlist[x].datum_start);
          const comparedEndDate = new Date(cartlist[x].datum_eind);
          // eslint-disable-next-line max-depth
          if ((comparedStartDate >= startDate && comparedStartDate <= endDate)
            || (comparedEndDate >= startDate && comparedEndDate <= endDate)){
            overlap = true;
          }
        }
      }
    }
    return overlap;
  }

  /**
   * Save the form values of the first step to the backend.
   */
  private async onSaveFirstStep(values: FormValues, redirect?: string): Promise<void> {
    await this.getCheckout(false, values);
    this.setState({ ...this.state, error: null });
    await this.post('Checkout', 'saveSession', { form: values });
    if (redirect){
      this.redirectTo(redirect);
    }
  }

  /**
   * Submit the final form.
   */
  private async onSubmit(values: FormValues): Promise<void> {
    await this.getCheckout(false, values);
    if (this.state.cartList.length === 0 || this.state.error){
      return;
    }
    this.setState({ ...this.state, error: null });
    values.couponCode = values.total.appliedCode;
    const { redirectTo }: CheckoutSubmitResponse = await this.post(
      'CheckoutFormProcessor',
      'submitOrder',
      { form: values }
    );

    this.redirectTo(redirectTo);
  }

  /**
   * Redirect to the url return by the backend. This is either
   * the payment url or the thankyou page in case the user doesn't need
   * to pay for the reservation.
   */
  private redirectTo(url: string): void {
    window.location.href = url;
  }

  /**
   * Generic ajax method, when the ajax call returns an error. The
   * error is passed to the state and displayed in a red alert on top of the page.
   */
  private async post(controller: string, action: string, values?: any): Promise<JQuery.jqXHR> {
    try {
      return await $.ajax({
        url: ajaxLink,
        dataType: 'json',
        method: 'post',
        data: {
          action: 'lyceo',
          controller: controller,
          nonce: ajaxNonce,
          method: action,
          postId: postID,
          ...values,
        },
      });
    } catch ({ responseJSON, responseText }) {
      this.showError((responseJSON && responseJSON.errors) || responseText);
    } finally {
      ScrollTopComponent.scrollToTop();
    }
  }

  /**
   * Scroll to the top of the page and pass the error to the state
   * so it will be displayed at the top of the form.
   */
  private showError(error: string): void {
    this.setState({
      ...this.state,
      error: error,
    });
  }

  /**
   * Helper method that gets be passed to the CouponForm. This way
   * the child component set the updated cart total to this parent
   * component.
   */
  private async setTotal(total: Total): Promise<void> {
    this.setState({
      ...this.state,
      total: total,
    });
    await this.post('Checkout', 'saveTotal', { total: total });
  }

  /**
   * Specify the 2 form steps. Each form step is wrapped in a Field element.
   * The Formik Wizard contains the buttons and stepper to navigate to the
   * next form step.
   */
  public render(): JSX.Element {
    const { donation, submenu, pageExtSearchResults, cartList, total, orderType, session, error } = this.state;

    // Show a placeholder while fetching the checkout state.
    if (!cartList) {
      return <CheckoutPlaceholder/>;
    }

    // Show a message in case the cart is empty.
    if (cartList.length === 0) {
      return <EmptyShoppingCart pageExtSearchResults={pageExtSearchResults}/>;
    }

    // Render the form steps.
    return (
      <Wizard
        isPreRegisterOnly={this.state.isPreRegisterOnly}
        initialValues={session}
        error={error}
        onSubmit={(formValues: FormValues): Promise<void> => this.onSubmit(formValues)}
        notice={this.state.notice}
        cartList={cartList}
        total={total}
        orderType={orderType}
        submenu={submenu}
        setTotal={(t: Total): Promise<void> => this.setTotal(t)}
        saveValues={(values: FormValues, redirect: string|undefined): Promise<void> => this.onSaveFirstStep(values, redirect)}>

        {/* Form step 1 */}
        <Field onSubmit={(values: FormValues): Promise<void> => this.onSaveFirstStep(values)}>
          {({ form }: FieldProps<FormValues>): JSX.Element => (<Fragment>
            <CartList isPreRegisterOnly={this.state.isPreRegisterOnly}
              cartList={cartList}/>
            {/*{ Only render the donation when the user pays for the training }*/}
            { (!this.state.isPreRegisterOnly && donation.items.length > 0)
            && <DonationComponent {...donation} form={form}/>}
          </Fragment>)}
        </Field>

        {/* Form step 2 */}
        <Field>
          {({ form }: FieldProps<FormValues>): JSX.Element => (<Fragment>
            <h5 className="checkout__title">
              Uw Gegevens
            </h5>
            <div className={'checkout__form pt-3'}>
              <CaretakerFormSection/>
              <PupilFormSection/>

              <div className="extra-info extra-info__show">
                <div className="row px-3 pb-4">
                  <div className="col-sm-12">
                    {/* eslint-disable-next-line max-len */}
                    * Let op: het e-mailadres van de leerling wordt gebruikt voor het versturen van aanvullende informatie. Controleer dit adres.
                  </div>
                </div>
              </div>
              <div className="px-3">
                <h5>Adres</h5>
                <AddressFields group="address" form={form}/>
              </div>

              { !this.state.isPreRegisterOnly
              && <InvoiceSection/>}

              <div className="m-3">
                <h5>School</h5>
                <div className="row">
                  <div className="col-sm-12">
                    <Input name="school"
                      placeholder="Naam school"
                      validate={validateRequired}/>
                  </div>
                </div>
                <div className="row mt-3">
                  <div className="col-sm-12">
                    <Checkbox name="newsletter" label="Ik ontvang graag de Lyceo nieuwsbrief"/>
                  </div>
                  <div className="col-sm-12">
                    <Checkbox name='terms'
                      label={[
                        <strong key={1}>
                          Ik ga akkoord met de <a href="/algemene-voorwaarden/" target="_blank">algemene voorwaarden</a>
                        </strong>,
                      ]}
                      validate={validateRequired}/>
                  </div>

                </div>
              </div>
            </div>
          </Fragment>)}
        </Field>
      </Wizard>
    );
  }
}

